import { HttpClient } from '../api/HttpClient';
import { API_ENDPOINTS } from '../api/ApiConfig';
import type {
  Banner,
  BannerCreateRequest,
  BannerUpdateRequest,
  BannerQueryParams,
  BannersData,
} from '../../types/api/banner';

/**
 * BannerService
 *
 * Handles:
 * - Banner CRUD operations (admin)
 * - Public banner listing
 */
export class BannerService {
  private httpClient: HttpClient;

  constructor() {
    this.httpClient = HttpClient.getInstance();
  }

  /**
   * Create banner with image upload (multipart/form-data)
   * Parts:
   * - data: JSON string (BannerCreateRequest)
   * - image: File
   */
  async createBannerWithImage(
    payload: BannerCreateRequest,
    imageFile: File
  ): Promise<Banner> {
    const formData = new FormData();

    const jsonBlob = new Blob([JSON.stringify(payload)], {
      type: 'application/json',
    });
    formData.append('data', jsonBlob, 'data.json');
    formData.append('image', imageFile, imageFile.name);

    const response = await this.httpClient.request<Banner>({
      method: 'POST',
      url: API_ENDPOINTS.BANNERS.ADMIN_CREATE_MULTIPART,
      data: formData,
    });

    if (!response.success) {
      throw new Error(response.message || 'Failed to create banner');
    }

    return response.data;
  }

  /**
   * Create banner using JSON-only endpoint (imageUrl required)
   */
  async createBannerWithJson(payload: BannerCreateRequest): Promise<Banner> {
    const response = await this.httpClient.post<Banner>(
      API_ENDPOINTS.BANNERS.ADMIN_CREATE_JSON,
      payload
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to create banner');
    }

    return response.data;
  }

  /**
   * Update existing banner with optional new image (multipart/form-data)
   */
  async updateBannerWithImage(
    bannerId: string,
    payload: BannerUpdateRequest,
    imageFile?: File
  ): Promise<Banner> {
    const formData = new FormData();

    const jsonBlob = new Blob([JSON.stringify(payload)], {
      type: 'application/json',
    });
    formData.append('data', jsonBlob, 'data.json');

    if (imageFile) {
      formData.append('image', imageFile, imageFile.name);
    }

    const response = await this.httpClient.request<Banner>({
      method: 'PUT',
      url: API_ENDPOINTS.BANNERS.ADMIN_UPDATE_MULTIPART(bannerId),
      data: formData,
    });

    if (!response.success) {
      throw new Error(response.message || 'Failed to update banner');
    }

    return response.data;
  }

  /**
   * Update banner using JSON-only endpoint
   */
  async updateBannerWithJson(
    bannerId: string,
    payload: BannerUpdateRequest
  ): Promise<Banner> {
    const response = await this.httpClient.put<Banner>(
      API_ENDPOINTS.BANNERS.ADMIN_UPDATE_JSON(bannerId),
      payload
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to update banner');
    }

    return response.data;
  }

  /**
   * Get banner by ID (admin)
   */
  async getBanner(bannerId: string): Promise<Banner> {
    const response = await this.httpClient.get<Banner>(
      API_ENDPOINTS.BANNERS.ADMIN_DETAIL(bannerId)
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch banner');
    }

    return response.data;
  }

  /**
   * List banners for admin with pagination & filters
   */
  async getBanners(params: BannerQueryParams = {}): Promise<BannersData> {
    const queryParams = new URLSearchParams();

    if (params.page !== undefined) queryParams.append('page', String(params.page));
    if (params.size !== undefined) queryParams.append('size', String(params.size));
    if (params.isActive !== undefined) queryParams.append('isActive', String(params.isActive));
    if (params.isFeatured !== undefined) {
      queryParams.append('isFeatured', String(params.isFeatured));
    }

    const url = `${API_ENDPOINTS.BANNERS.ADMIN_LIST}${
      queryParams.toString() ? `?${queryParams.toString()}` : ''
    }`;

    const response = await this.httpClient.get<BannersData>(url);

    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch banners');
    }

    return response.data;
  }

  /**
   * Delete banner (admin)
   */
  async deleteBanner(bannerId: string): Promise<void> {
    const response = await this.httpClient.delete(
      API_ENDPOINTS.BANNERS.ADMIN_DELETE(bannerId)
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to delete banner');
    }
  }

  /**
   * Activate banner (admin)
   */
  async activateBanner(bannerId: string): Promise<void> {
    const response = await this.httpClient.put(
      API_ENDPOINTS.BANNERS.ADMIN_ACTIVATE(bannerId)
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to activate banner');
    }
  }

  /**
   * Deactivate banner (admin)
   */
  async deactivateBanner(bannerId: string): Promise<void> {
    const response = await this.httpClient.put(
      API_ENDPOINTS.BANNERS.ADMIN_DEACTIVATE(bannerId)
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to deactivate banner');
    }
  }
}

